import React, { useEffect, useMemo, useState } from 'react';
import {
  Check,
  Edit,
  Phone,
  Plus,
  Save,
  Search,
  ShieldCheck,
  UserCog,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { AdminRole, AdminUser, AdminUserRequest, Technician } from '../types';

const emptyForm: AdminUserRequest = {
  name: '',
  mobile: '',
  email: '',
  isActive: true,
  roles: [],
  technicianIds: [],
};

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [form, setForm] = useState<AdminUserRequest>(emptyForm);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersResponse, rolesResponse, techniciansResponse] = await Promise.all([
        apiService.getAdminUsers(),
        apiService.getAdminRoles(),
        apiService.getTechnicians(),
      ]);

      if (usersResponse.isSuccess) setUsers(usersResponse.entity || []);
      else toast.error(usersResponse.message || 'Failed to load users');

      if (rolesResponse.isSuccess) setRoles(rolesResponse.entity || []);
      else toast.error(rolesResponse.message || 'Failed to load roles');

      if (techniciansResponse.isSuccess) setTechnicians(techniciansResponse.entity || []);
    } catch (error) {
      toast.error('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;

    return users.filter(user =>
      user.name?.toLowerCase().includes(term) ||
      user.mobile?.includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.roles?.some(role => role.toLowerCase().includes(term))
    );
  }, [search, users]);

  const isTechnicianRoleSelected = form.roles.some(role => role.toLowerCase() === 'technician');

  const resetForm = () => {
    setForm(emptyForm);
    setEditingUserId(null);
  };

  const editUser = (user: AdminUser) => {
    setEditingUserId(user.id);
    setForm({
      name: user.name || '',
      mobile: user.mobile || '',
      email: user.email || '',
      isActive: user.isActive,
      roles: user.roles || [],
      technicianIds: user.technicianIds || [],
    });
  };

  const toggleRole = (roleName: string) => {
    setForm(prev => {
      const exists = prev.roles.some(role => role.toLowerCase() === roleName.toLowerCase());
      const nextRoles = exists
        ? prev.roles.filter(role => role.toLowerCase() !== roleName.toLowerCase())
        : [...prev.roles, roleName];

      const hasTechnician = nextRoles.some(role => role.toLowerCase() === 'technician');
      return {
        ...prev,
        roles: nextRoles,
        technicianIds: hasTechnician ? prev.technicianIds : [],
      };
    });
  };

  const toggleTechnician = (technicianId: number) => {
    setForm(prev => ({
      ...prev,
      technicianIds: prev.technicianIds.includes(technicianId)
        ? prev.technicianIds.filter(id => id !== technicianId)
        : [...prev.technicianIds, technicianId],
    }));
  };

  const saveUser = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }

    const mobile = form.mobile.replace(/\D/g, '').slice(-10);
    if (mobile.length !== 10) {
      toast.error('Enter a valid 10 digit mobile number');
      return;
    }

    if (form.roles.length === 0) {
      toast.error('Select at least one role');
      return;
    }

    if (isTechnicianRoleSelected && form.technicianIds.length === 0) {
      toast.error('Map at least one technician for Technician role');
      return;
    }

    setSaving(true);
    try {
      const payload: AdminUserRequest = {
        ...form,
        mobile,
        technicianIds: isTechnicianRoleSelected ? form.technicianIds : [],
      };

      const response = editingUserId
        ? await apiService.updateAdminUser(editingUserId, payload)
        : await apiService.createAdminUser(payload);

      if (response.isSuccess) {
        toast.success(editingUserId ? 'User updated' : 'User created');
        resetForm();
        await loadData();
      } else {
        toast.error(response.message || response.errors?.join(', ') || 'Failed to save user');
      }
    } catch (error: any) {
      toast.error(apiService.handleError(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (user: AdminUser) => {
    try {
      const response = await apiService.setAdminUserStatus(user.id, !user.isActive);
      if (response.isSuccess) {
        toast.success(user.isActive ? 'User deactivated' : 'User activated');
        await loadData();
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error: any) {
      toast.error(apiService.handleError(error));
    }
  };

  const technicianName = (id: number) => technicians.find(tech => tech.id === id)?.name || `Technician #${id}`;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <UserCog className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Users</h1>
            <p className="text-xs text-gray-500">Manage admin portal access, roles, and technician mappings</p>
          </div>
        </div>
        <button
          onClick={resetForm}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          New User
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_390px] gap-5">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Admin Portal Users</h2>
              <p className="text-xs text-gray-500">{filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'} found</p>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin h-8 w-8 rounded-full border-b-2 border-blue-600" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <UserCog className="h-10 w-10 mb-2" />
              <p className="text-sm">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">User</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Roles</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Technician Mapping</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-sm text-gray-900">{user.name}</div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                          <Phone className="h-3 w-3" />
                          {user.mobile}
                        </div>
                        {user.email && <div className="text-xs text-gray-400 mt-0.5">{user.email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {(user.roles || []).map(role => (
                            <span key={role} className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-50 text-blue-700">
                              {role}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {user.technicianIds?.length ? (
                          <div className="space-y-1">
                            {user.technicianIds.map(id => (
                              <div key={id} className="text-xs text-gray-600">{technicianName(id)}</div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${user.isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => editUser(user)}
                            className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                            title="Edit user"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => toggleStatus(user)}
                            className={`p-2 rounded-lg ${user.isActive ? 'text-gray-500 hover:text-red-600 hover:bg-red-50' : 'text-gray-500 hover:text-green-600 hover:bg-green-50'}`}
                            title={user.isActive ? 'Deactivate user' : 'Activate user'}
                          >
                            {user.isActive ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <form onSubmit={saveUser} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 h-fit space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900">{editingUserId ? 'Edit User' : 'Create User'}</h2>
              <p className="text-xs text-gray-500">OTP login access is based on this mobile number</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="User name"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Mobile</label>
            <input
              value={form.mobile}
              onChange={(e) => setForm(prev => ({ ...prev, mobile: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="10 digit mobile"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Email</label>
            <input
              value={form.email || ''}
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Roles</label>
            <div className="grid grid-cols-1 gap-2">
              {roles.map(role => {
                const selected = form.roles.some(item => item.toLowerCase() === role.name.toLowerCase());
                return (
                  <button
                    type="button"
                    key={role.id}
                    onClick={() => toggleRole(role.name)}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-colors ${
                      selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">{role.name}</span>
                    {selected && <Check className="h-4 w-4" />}
                  </button>
                );
              })}
            </div>
          </div>

          {isTechnicianRoleSelected && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Technician Mapping</label>
              <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {technicians.map(tech => {
                  const selected = form.technicianIds.includes(tech.id);
                  return (
                    <button
                      type="button"
                      key={tech.id}
                      onClick={() => toggleTechnician(tech.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm ${selected ? 'bg-green-50 text-green-700' : 'hover:bg-gray-50 text-gray-700'}`}
                    >
                      <span>
                        <span className="font-medium">{tech.name}</span>
                        <span className="block text-xs text-gray-400">{tech.mobile}</span>
                      </span>
                      {selected && <Check className="h-4 w-4" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <label className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
            <span className="text-sm font-medium text-gray-700">Active user</span>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </label>

          <div className="flex items-center gap-2 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : editingUserId ? 'Update User' : 'Create User'}
            </button>
            {editingUserId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersPage;
